import React, { useEffect, useState } from 'react';
import styles from './DpTableDeviceSection.module.scss';
import ReactInputSelectV2 from '../../ProductDetailsV3/ReactInputSelectV2/ReactInputSelectV2';
import RichText from '../../richtext';
import { countZero, getElementMaximumHeight } from '../../../utils/utils';
import { getOptionsList } from '../DpTableUtils';
import SkeletonLoader from '../../SkeletonLoader/SkeletonLoader';
import ToolTip from '../../ToolTip/ToolTip';

const DpTableDeviceSection = ({
  productData, //products sb data
  mainContent, // whole content
  activeTab,
  tabIndex,
  indexKey, //products index
  cardsLength,
  deviceModelsList, //list of only device models ex: {id: Apple iphone 16,name: Apple iphone 16}
  deviceDetailsList, // All details of specific modal like color, memory and prices
  setDeviceDetails, // call back to update the selected device details in dpProduct.tsx
  fetchDeviceDetails // call back from dpTabCOntainer to trigger hardwares API call to get model's varient and price details on switch of models
}: any) => {
  const [updatedDeviceList, setUpdatedDeviceList] = useState<any[]>([]); //Updated device models list with its respective device images
  const [selectedDeviceModal, setSelectedDeviceModal] = useState<any>();
  const [selectedStorage, setSelectedStorage] = useState<any>();
  const [selectedColor, setSelectedColor] = useState<any>();
  const [selectedVarientData, setSelectedVarientData] = useState<any>(); // details of the selected device varient like price and all
  const [basePrice, setBasePrice] = useState(0);
  const [discountPrice, setDiscountPrice] = useState(0);
  const [availabilityStatus, setAvailabilityStatus] = useState<any>();
  const deviceDetails = deviceDetailsList?.[selectedDeviceModal?.id] ?? false; //picking details of the selected modal from the whole list
  const [storageOptions, setStorageOptions] = useState<any[]>([]);
  const [colorOptions, setColorOptions] = useState<any[]>([]);
  const [colorWithHexCode, setcolorWithHexCode] = useState<any>();

  const onModalChange = (e: any) => {
    if (e?.value) {
      setSelectedDeviceModal(e?.value);
    }
    if (e?.value?.id && e?.value?.id !== selectedDeviceModal?.id) {
      fetchDeviceDetails(e?.value?.id);
    }
  };

  const onStorageChange = (e: any) => {
    if (e?.value) {
      setSelectedStorage(e?.value);
    }
  };

  const onColorChange = (e: any) => {
    if (e?.value) {
      setSelectedColor(e?.value);
    }
  };

  // to update the display price based on the varients selected
  const setDevicePrices = (color: string, memory: string) => {
    const varient = `${color}-${memory}`;
    const varientDetails = deviceDetails?.deviceVarientsList?.[varient];
    if (varientDetails) {
      if (varientDetails?.hardwarePrices?.smartUpgradeInstallments?.monthlyInstallment?.value) {
        setDiscountPrice(
          varientDetails?.hardwarePrices?.smartUpgradeInstallments?.monthlyInstallment?.value
        );
      }
      if (varientDetails?.hardwarePrices?.installments?.monthlyInstallment?.value) {
        setBasePrice(varientDetails?.hardwarePrices?.installments?.monthlyInstallment?.value);
      }
      let availableText, defaultAvailableText;
      (mainContent?.availabilityDetails || [])?.forEach((data: any) => {
        if (data?.key === varientDetails?.availabilityStatus) {
          availableText = data?.value ?? '';
        } else if (data?.key === 'dd_delivery_details') {
          defaultAvailableText = data?.value ?? '';
        }
      });

      if (availableText || defaultAvailableText)
        setAvailabilityStatus({
          availabilityText: availableText,
          defaultAvailabilityText: defaultAvailableText
        });
    }
  };

  useEffect(() => {
    if (mainContent?.colorCode?.[0]?.reference?.content?.colorCode) {
      const colorWithCode: any = [];
      (mainContent?.colorCode?.[0]?.reference?.content?.colorCode || [])?.forEach((color: any) => {
        colorWithCode[`${color?.colorKey}`] = color?.hexCode?.color;
      });
      colorWithCode &&
        Object?.keys(colorWithCode)?.length > 0 &&
        setcolorWithHexCode(colorWithCode);
    }
  }, [mainContent]);

  //default selecting the modal once we get the response from device list API
  useEffect(() => {
    if (
      deviceModelsList?.length > 0 &&
      mainContent?.deviceReference?.[0]?.reference?.content?.body?.length > 0
    ) {
      //to hide the selected device from devicemodelist dropdown
      const ignoreDeviceList = mainContent?.tabContainer?.[activeTab]?.ignoreDevices ?? "";
      deviceModelsList = deviceModelsList?.filter(
        (items: any) => ignoreDeviceList?.indexOf(items?.id) === -1
      );
      const newDeviceList = (deviceModelsList || [])?.map((products: any) => {
        const deviceContent =
          mainContent?.deviceReference?.[0]?.reference?.content?.body?.filter(
            (items: any) => items?.defaultVariantId === products?.id
          )?.[0] ?? [];
        return {
          ...products,
          icon: deviceContent?.images?.[0]?.filename
        };
      });
      if (newDeviceList?.length > 0) {
        setUpdatedDeviceList(newDeviceList);
        const defaultModalValue = newDeviceList?.filter(
          (device: any) => device?.id === mainContent?.tabContainer?.[activeTab]?.defaultDevice
        );
        if (defaultModalValue?.length > 0) {
          setSelectedDeviceModal(defaultModalValue?.[0]);
        }
      }
    }
  }, [deviceModelsList]);

  //to update the default selected color and memory once we get hardware details of selected model
  useEffect(() => {
    if (deviceDetails && Object.keys(deviceDetails)?.length > 0) {
      setSelectedStorage(getOptionsList([deviceDetails?.defaultMemorySelected])?.[0]);
      setSelectedColor({
        ...getOptionsList([deviceDetails?.defaultColorSelected])?.[0],
        code: colorWithHexCode[`${deviceDetails?.defaultColorSelected}`] ?? ''
      });
      const updatedStorageList = getCombination(
        deviceDetails?.deviceVarientsList,
        deviceDetails?.defaultColorSelected
      );
      setStorageOptions(getOptionsList(updatedStorageList));

      const updatedColorOptions = getCombination(
        deviceDetails?.deviceVarientsList,
        deviceDetails?.defaultMemorySelected
      );
      const colorsList = getOptionsList(updatedColorOptions);
      const updateColorList = (colorsList || [])?.map((color: any) => {
        return {
          ...color,
          code: colorWithHexCode[`${color?.id}`]
        };
      });
      setColorOptions(updateColorList);
      setDevicePrices(deviceDetails?.defaultColorSelected, deviceDetails?.defaultMemorySelected);
    }
  }, [deviceDetailsList, selectedDeviceModal]);

  //call back to update details in dpProduct and updating price on change of color or memory
  useEffect(() => {
    const imgURL = (updatedDeviceList || [])?.filter(
      (device: any) => device?.id === selectedDeviceModal?.id
    );
    if (selectedColor && selectedStorage) {
      const varient = `${selectedColor?.id}-${selectedStorage?.id}`;
      const varientDetails = deviceDetails?.deviceVarientsList?.[varient];
      setSelectedVarientData(varientDetails);
      setDevicePrices(selectedColor?.id, selectedStorage?.id);
      setDeviceDetails({
        selectedDeviceModal: selectedDeviceModal?.id,
        selectedColor: selectedColor?.id,
        selectedStorage: selectedStorage?.id,
        selectedImageUrl: imgURL?.[0]?.icon ?? '',
        selectedVarientDetails: varientDetails
      });
    }
    setTimeout(() => {
      findHeight();
    }, 100);
    window?.addEventListener('load', findHeight);
    window?.addEventListener('resize', findHeight);
    return () => {
      window.removeEventListener('resize', findHeight);
      window.removeEventListener('load', findHeight);
    };
  }, [selectedColor, selectedStorage]);

  useEffect(() => {
    if (deviceDetails?.deviceVarientsList) {
      const newStorageList = getCombination(deviceDetails?.deviceVarientsList, selectedColor?.id);
      if (newStorageList?.length > 0) {
        setStorageOptions(getOptionsList(newStorageList));
      }
    }
  }, [selectedColor]);

  useEffect(() => {
    if (deviceDetails?.deviceVarientsList) {
      const newColorList = getCombination(deviceDetails?.deviceVarientsList, selectedStorage?.id);
      const colorsList = getOptionsList(newColorList);
      const updateColorList = (colorsList || [])?.map((color: any) => {
        return {
          ...color,
          code: colorWithHexCode[`${color?.id}`]
        };
      });
      if (newColorList?.length > 0) {
        setColorOptions(updateColorList);
      }
    }
  }, [selectedStorage]);

  const getCombination = (array: any, partialKey: any) => {
    const matchedArray: any = [];
    const list = array && Object?.entries(array);
    list?.forEach((key: any) => {
      const keys = key?.[0]?.split('-');
      if (keys?.[0] === partialKey) {
        matchedArray.push(keys?.[1]);
      } else if (keys?.[1] === partialKey) {
        matchedArray.push(keys?.[0]);
      }
    });
    return matchedArray;
  };

  const findHeight = () => {
    getElementMaximumHeight(`deviceSection${tabIndex}`, cardsLength);
    getElementMaximumHeight(`deviceDetailsSection${tabIndex}`, cardsLength);
    getElementMaximumHeight(`devicePriceSection${tabIndex}`, cardsLength);
    getElementMaximumHeight(`deviceAvailSection${tabIndex}`, cardsLength);
    getElementMaximumHeight(`deviceSectionLoader${tabIndex}`, cardsLength);
    getElementMaximumHeight(`deviceModalSelector${tabIndex}`, cardsLength);
    getElementMaximumHeight(`deviceSpecsSelector${tabIndex}`, cardsLength);
  };

  return (
    <>
      {updatedDeviceList?.length > 0 && deviceDetails && Object.keys(deviceDetails)?.length > 0 ? (
        <div className={styles.deviceSection} id={`deviceSection${tabIndex}${indexKey}`}>
          <div
            className={styles.deviceDetailsContainer}
            id={`deviceDetailsSection${tabIndex}${indexKey}`}
          >
            {updatedDeviceList?.length > 0 && (
              <div
                className={styles.deviceModalSelector}
                id={`deviceModalSelector${tabIndex}${indexKey}`}
              >
                <ReactInputSelectV2
                  name='deviceModal'
                  content={{ label: '', errorMessage: '' }}
                  options={updatedDeviceList}
                  optionsValue='id'
                  optionsLabel='name'
                  onChange={onModalChange}
                  isSearchable={false}
                  isClearable={false}
                  defaultValue={selectedDeviceModal}
                  valueContainerVarient='deviceModalValue'
                />
              </div>
            )}
            <div className='row' id={`deviceSpecsSelector${tabIndex}${indexKey}`}>
              {storageOptions?.length > 0 && selectedStorage && (
                <div className='col-5'>
                  <ReactInputSelectV2
                    name='deviceStorage'
                    content={{ label: '', errorMessage: '' }}
                    options={storageOptions}
                    optionsValue='id'
                    optionsLabel='name'
                    onChange={onStorageChange}
                    isSearchable={false}
                    isClearable={false}
                    defaultValue={selectedStorage}
                    updateValue={selectedStorage}
                    valueContainerVarient='deviceStorageValue'
                  />
                </div>
              )}
              {colorOptions?.length > 0 && selectedColor && (
                <div className='col-7'>
                  <ReactInputSelectV2
                    name='deviceColor'
                    content={{ label: '', errorMessage: '' }}
                    options={colorOptions}
                    optionsValue='id'
                    optionsLabel='name'
                    onChange={onColorChange}
                    isSearchable={false}
                    isClearable={false}
                    defaultValue={selectedColor}
                    updateValue={selectedColor}
                    valueContainerVarient='deviceColorValue'
                    optionsContainerVarient='deviceColorOptions'
                  />
                </div>
              )}
            </div>
          </div>
          {discountPrice > 0 && (
            <>
              <hr />
              <div
                className={styles.devicePriceContainer}
                id={`devicePriceSection${tabIndex}${indexKey}`}
              >
                <div className={styles.devicePriceSection}>
                  <img src={mainContent?.discountIcon?.filename} alt='' width={24} height={24} />
                  <div className={styles.deviceDiscountedPrice}>
                    {countZero(discountPrice)}
                    <span className={styles.month}>{mainContent?.contractLabel}</span>
                  </div>
                  <div className={styles.deviceBasePrice}>{countZero(basePrice)}</div>
                </div>
                {productData?.deviceDiscountText?.length > 0 && (
                  <div className={styles.devicePriceDescription}>
                    <RichText content={productData?.deviceDiscountText?.[0]?.text} />
                    {productData?.deviceDiscountText?.[0]?.tooltipTxt && (
                      <ToolTip
                        content={
                          <RichText content={productData?.deviceDiscountText?.[0]?.tooltipTxt} />
                        }
                      />
                    )}
                  </div>
                )}
              </div>
            </>
          )}
          <hr />
          {/* device avialibility section */}
          {availabilityStatus && (
            <div
              className={styles.deviceAvailability}
              id={`deviceAvailSection${tabIndex}${indexKey}`}
            >
              <img
                src={
                  selectedVarientData?.availabilityStatus === 'NOT_AVAILABLE'
                    ? mainContent?.notAvailableIcon?.filename
                    : selectedVarientData?.availabilityStatus &&
                      availabilityStatus?.availabilityText &&
                      selectedVarientData?.availabilityStatus !== 'AVAILABLE'
                    ? mainContent?.delayedAvailableIcon?.filename
                    : mainContent?.availableIcon?.filename
                }
                alt=''
                width={16}
                height={16}
              />
              {selectedVarientData?.availabilityStatus && availabilityStatus?.availabilityText
                ? availabilityStatus?.availabilityText
                : availabilityStatus?.defaultAvailabilityText}
            </div>
          )}
        </div>
      ) : (
        <div className={styles.deviceSection} id={`deviceSectionLoader${tabIndex}${indexKey}`}>
          <>
            <SkeletonLoader stylename={styles.skeletontittle} count={1} />
            <SkeletonLoader stylename={styles.skeletonline} count={5} />
            <SkeletonLoader stylename={styles.skeletontittle} count={1} />
            <SkeletonLoader stylename={styles.skeletonline} count={5} />
          </>
        </div>
      )}
    </>
  );
};

export default DpTableDeviceSection;
